import React, { useState, useEffect, useMemo } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { 
  PlusIcon, 
  XIcon, 
  Info,
  AlertCircle,
  CheckCircle,
  CodeIcon,
  WandIcon,
  GripVerticalIcon,
  ChevronDownIcon,
  FlaskConicalIcon,
  Loader2Icon,
  FoldersIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Tooltip } from "../ui/tooltip";
import { RoutingService } from "../../services/RoutingService";
import { 
  mapOperatorToBackend, 
  buildConditionExpression,
  convertConditionToBackend,
  type RoutingRuleCondition as CentralizedRoutingRuleCondition 
} from "../../utils/routingRuleExpressions";

type BoolOp = "AND" | "OR";

type RouteTarget = { 
  type: "agent" | "action" | "llm" | "none"; 
  agent_id?: number; 
  action_id?: number;
};

type RoutingRuleCondition = {
  id?: string | number;
  target_type: "request" | "state";
  variable_path: string;
  operator: "equals" | "not_equals" | "gt" | "gte" | "lt" | "lte" | "like" | "matches" | "sem_match" | "sem_similarity" | "classify" | "not_classify" | "sentiment" | "is_null" | "is_not_null";
  value_text?: string;
  value_number?: number;
  value_bool?: boolean;
  comparison_op?: ">" | ">=" | "=" | "<=" | "<" | "IS" | "IS_NOT";
  threshold_number?: number;
  enabled?: boolean;
  logical_op?: "AND" | "OR";
  group_start?: number;
  group_end?: number;
};

type RoutingRule = { 
  id?: string; 
  if: string; 
  condition_expression_template?: string;
  conditions?: RoutingRuleCondition[];
  route: RouteTarget; 
  enabled?: boolean; 
  label?: string;
};

interface RuleCondition {
  id: string;
  variable: string;
  operator: string;
  value: string;
  comparisonOp?: string;
  comparedValue?: string;
}

interface RuleGroup {
  id: string;
  op: BoolOp;
  not?: boolean;
  children: Array<RuleGroup | RuleCondition>;
}

interface SubAgent {
  id: number;
  agent_name: string;
}

interface Action {
  id: number;
  action_name: string;
}

interface FormField {
  name: string;
  description: string;
  type: string;
  readScope: string;
  writeScope: string;
}

interface RoutingRuleEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rule: RoutingRule) => void;
  editingRule: RoutingRule | null;
  subAgents: SubAgent[];
  actions: Action[];
  formFields: FormField[];
  agent: any;
  apiKey: ApiKeyOrGetter;
}

const RoutingRuleEditor: React.FC<RoutingRuleEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  editingRule,
  subAgents,
  actions,
  formFields,
  agent,
  apiKey,
}) => {
  const [activeTab, setActiveTab] = useState<"builder" | "advanced">("builder");
  const [routeTarget, setRouteTarget] = useState<RouteTarget>({ type: "agent" });
  const [advancedExpression, setAdvancedExpression] = useState<string>("");
  const [ruleTree, setRuleTree] = useState<RuleGroup>({
    id: "root",
    op: "AND",
    children: []
  });
  const [validationError, setValidationError] = useState<string>("");
  const [expressionPreview, setExpressionPreview] = useState<string>("");
  const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({});
  const [selectedAgentActions, setSelectedAgentActions] = useState<Action[]>([]);
  const [ruleLabel, setRuleLabel] = useState<string>("");
  const [hasUnsaved, setHasUnsaved] = useState<boolean>(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState<boolean>(false);
  
  // Drag and drop state - simplified architecture
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [activeDropZone, setActiveDropZone] = useState<{ groupId: string; index: number } | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  
  // Test state
  const [showTestPanel, setShowTestPanel] = useState<boolean>(false);
  const [testInputs, setTestInputs] = useState<Record<string, any>>({});
  const [testResult, setTestResult] = useState<{
    rule_passed: boolean;
    conditions: Array<{
      condition_index: number;
      passed: boolean;
      variable: string;
      operator: string;
      expected: any;
      actual: any;
      similarity?: number;
      threshold?: number;
      comparison_op?: string;
      predicted_class?: string;
      confidence?: number;
    }>;
  } | null>(null);
  const [testLoading, setTestLoading] = useState<boolean>(false);
  const [testError, setTestError] = useState<string>("");

  // Helper to generate unique IDs
  const generateId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Build tree from backend conditions using the template
  const buildTreeFromBackendConditions = (backendConditions: RoutingRuleCondition[], template?: string): RuleGroup => {
    console.log("🔄 Building tree from backend conditions:");
    console.log("  Number of conditions:", backendConditions.length);
    console.log("  Template provided:", !!template);
    console.log("  Template value:", template);
    console.log("  Backend conditions:", backendConditions.map((c, i) => ({
      i,
      var: `${c.target_type}.${c.variable_path}`,
      op: c.operator,
      logical_op: c.logical_op,
      group_start: c.group_start,
      group_end: c.group_end
    })));
    
    // Convert backend conditions to RuleCondition format
    const conditions: RuleCondition[] = backendConditions.map((cond) => ({
      id: generateId(),
      variable: `${cond.target_type}.${cond.variable_path}`,
      operator: (() => {
        const operatorMapping: Record<string, string> = {
          "equals": "==",
          "not_equals": "!=",
          "gt": ">",
          "gte": ">=",
          "lt": "<",
          "lte": "<=",
        };
        return operatorMapping[cond.operator] || cond.operator;
      })(),
      value: (() => {
        if (["sem_match", "sem_similarity", "like", "matches", "classify", "not_classify", "sentiment"].includes(cond.operator)) {
          return cond.value_text || "";
        }
        if (["gt", "gte", "lt", "lte"].includes(cond.operator)) {
          return cond.value_number?.toString() || "";
        }
        if (cond.value_bool !== undefined && cond.value_bool !== null) {
          return cond.value_bool.toString().toLowerCase();
        }
        if (cond.value_number !== undefined && cond.value_number !== null) {
          return cond.value_number.toString();
        }
        return cond.value_text || "";
      })(),
      comparisonOp: cond.comparison_op,
      comparedValue: cond.threshold_number?.toString(),
    }));

    if (conditions.length === 0) {
      return { id: "root", op: "AND", children: [] };
    }

    // If we have a template, use it to build the tree
    if (template && template.trim()) {
      console.log("  ✅ Using provided template");
      return parseTemplateToAST(template, conditions);
    }

    // No template provided - reconstruct from group_start/group_end metadata
    console.log("  ⚠️ No template provided, reconstructing from group_start/group_end");
    const reconstructedTemplate = reconstructTemplateFromGrouping(backendConditions);
    console.log("  Reconstructed template:", reconstructedTemplate);
    
    return parseTemplateToAST(reconstructedTemplate, conditions);
  };

  // Reconstruct template from group_start and group_end metadata
  const reconstructTemplateFromGrouping = (backendConditions: RoutingRuleCondition[]): string => {
    const parts: string[] = [];
    
    for (let i = 0; i < backendConditions.length; i++) {
      const cond = backendConditions[i];
      const groupStart = cond.group_start || 0;
      const groupEnd = cond.group_end || 0;
      const logicalOp = cond.logical_op;
      
      // Add logical operator before this condition (if not first)
      if (i > 0 && logicalOp) {
        parts.push(logicalOp);
      }
      
      // Add opening parens
      for (let p = 0; p < groupStart; p++) {
        parts.push("(");
      }
      
      // Add condition placeholder
      parts.push(`{${i}}`);
      
      // Add closing parens
      for (let p = 0; p < groupEnd; p++) {
        parts.push(")");
      }
    }
    
    return parts.join(" ").trim();
  };

  // Helper function to find a sub-agent by ID in the agent tree
  const findSubAgentById = (agentId: number, currentAgent: any): any => {
    if (!currentAgent || !currentAgent.sub_agents) return null;
    const directSubAgent = currentAgent.sub_agents.find((sa: any) => sa.id === agentId);
    if (directSubAgent) return directSubAgent;
    for (const subAgent of currentAgent.sub_agents) {
      const found = findSubAgentById(agentId, subAgent);
      if (found) return found;
    }
    return null;
  };

  // Available variables for conditions
  const availableVariables = useMemo(() => {
    const requestVars = [
      { value: "request.last_message", label: "request.last_message", description: "Most recent message content" },
      { value: "request.last_user_message", label: "request.last_user_message", description: "Most recent user message" },
      { value: "request.last_assistant_message", label: "request.last_assistant_message", description: "Most recent assistant message" },
    ];

    const stateVars = formFields.map(field => ({
      value: `state.${field.name}`,
      label: `state.${field.name}`,
      description: field.description || `${field.type} field`,
      fieldType: field.type,
    }));

    return [...requestVars, ...stateVars];
  }, [formFields]);

  // Map UI operators to backend operators - now using centralized function

  const getOperatorsForVariable = (variable: string) => {
    const varInfo = availableVariables.find(v => v.value === variable);
    const fieldType = (varInfo as any)?.fieldType || "string";
    
    const nullOps = [
      { value: "is_null", label: "is null" },
      { value: "is_not_null", label: "is not null" },
    ];
    
    const stringOps = [
      { value: "==", label: "equals" },
      { value: "!=", label: "not equals" },
      { value: "like", label: "contains" },
      { value: "matches", label: "regex match" },
      { value: "sem_match", label: "semantic match" },
      { value: "sem_similarity", label: "semantic similarity" },
      { value: "classify", label: "classify as" },
      { value: "not_classify", label: "not classify as" },
      { value: "sentiment", label: "sentiment" },
      ...nullOps,
    ];
    
    const numberOps = [
      { value: "==", label: "equals" },
      { value: "!=", label: "not equals" },
      { value: ">", label: "greater than" },
      { value: ">=", label: "greater than or equal" },
      { value: "<", label: "less than" },
      { value: "<=", label: "less than or equal" },
      ...nullOps,
    ];
    
    const booleanOps = [
      { value: "==", label: "equals" },
      { value: "!=", label: "not equals" },
      ...nullOps,
    ];
    
    if (variable.startsWith("request.")) {
      return stringOps;
    }
    
    if (fieldType === "number" || fieldType === "integer" || fieldType === "float") {
      return numberOps;
    } else if (fieldType === "boolean") {
      return booleanOps;
    } else {
      return stringOps;
    }
  };

  // Check if a node is a condition
  const isCondition = (node: RuleGroup | RuleCondition): node is RuleCondition => {
    return "variable" in node;
  };

  // Convert AST to expression string using centralized logic
  const toExpression = (node: RuleGroup | RuleCondition, isRoot = false): string => {
    if (isCondition(node)) {
      // Convert UI condition to backend format and use centralized expression builder
      const backendCondition: CentralizedRoutingRuleCondition = {
        target_type: node.variable.startsWith("state.") ? "state" : "request",
        variable_path: node.variable.startsWith("state.") ? node.variable.replace("state.", "") : node.variable.replace("request.", ""),
        operator: mapOperatorToBackend(node.operator) as any,
        value_text: ["like", "matches", "sem_match", "sem_similarity", "classify", "not_classify", "sentiment"].includes(node.operator) ? node.value : 
          (node.operator === "==" || node.operator === "!=") ? node.value : undefined,
        value_number: [">", ">=", "<", "<="].includes(node.operator) ? parseFloat(node.value) : undefined,
        value_bool: (node.value === "true" || node.value === "false" || node.value === "null") ? 
          (node.value === "null" ? null : node.value === "true") : undefined,
        comparison_op: node.operator === "sem_similarity" ? (node.comparisonOp || ">") as any : 
          node.operator === "sentiment" ? (node.comparisonOp || "IS") as any : undefined,
        threshold_number: node.operator === "sem_similarity" ? parseFloat(node.comparedValue || "0.85") : undefined,
        enabled: true,
        group_start: 0,
        group_end: 0,
      };

      return buildConditionExpression(backendCondition);
    }

    // It's a group
    const parts = node.children.map((c) => toExpression(c, false)).filter(Boolean);
    if (parts.length === 0) return "";
    
    const joined = parts.join(` ${node.op} `);
    const body = parts.length > 1 && !isRoot ? `(${joined})` : joined;
    
    return node.not ? `NOT ${body}` : body;
  };

  // Convert AST to template and conditions array
  // Also track the logical operators for each condition
  const toTemplateAndConditions = (root: RuleGroup): { 
    template: string; 
    conditions: RuleCondition[];
    logicalOps: (BoolOp | undefined)[];
  } => {
    const conditions: RuleCondition[] = [];
    const logicalOps: (BoolOp | undefined)[] = [];

    const walk = (node: RuleGroup | RuleCondition, isRoot = false, parentOp?: BoolOp): string => {
      if (isCondition(node)) {
        const i = conditions.length;
        conditions.push(node);
        logicalOps.push(i === 0 ? undefined : parentOp);
        return `{${i}}`;
      }

      // It's a group
      const childExprs: string[] = [];
      for (const child of node.children) {
        const expr = walk(child, false, node.op);
        if (expr) childExprs.push(expr);
      }

      if (childExprs.length === 0) return "";

      const joined = childExprs.join(` ${node.op} `);
      const body = (isRoot || childExprs.length === 1) ? joined : `(${joined})`;
      
      return node.not ? `NOT ${body}` : body;
    };

    const template = walk(root, true).trim();
    return { template, conditions, logicalOps };
  };

  // Parse template into AST using recursive descent parser
  // Template example: "{0} AND ( {1} OR {2} OR {3} )"
  const parseTemplateToAST = (template: string, conditions: RuleCondition[]): RuleGroup => {
    console.log("🔄 Parsing template:", template);
    console.log("🔄 With", conditions.length, "conditions");
    
    // Tokenize the template
    const tokens: Array<{ type: "paren_open" | "paren_close" | "and" | "or" | "placeholder"; value: string }> = [];
    let i = 0;
    
    while (i < template.length) {
      // Skip whitespace
      if (/\s/.test(template[i])) {
        i++;
        continue;
      }
      
      // Parentheses
      if (template[i] === "(") {
        tokens.push({ type: "paren_open", value: "(" });
        i++;
        continue;
      }
      if (template[i] === ")") {
        tokens.push({ type: "paren_close", value: ")" });
        i++;
        continue;
      }
      
      // AND/OR operators
      if (template.substring(i, i + 3) === "AND") {
        tokens.push({ type: "and", value: "AND" });
        i += 3;
        continue;
      }
      if (template.substring(i, i + 2) === "OR") {
        tokens.push({ type: "or", value: "OR" });
        i += 2;
        continue;
      }
      
      // Placeholders like {0}, {1}, etc.
      if (template[i] === "{") {
        const endBrace = template.indexOf("}", i);
        if (endBrace > i) {
          const placeholder = template.substring(i, endBrace + 1);
          tokens.push({ type: "placeholder", value: placeholder });
          i = endBrace + 1;
          continue;
        }
      }
      
      i++;
    }
    
    console.log("🔄 Tokens:", tokens.map(t => t.value).join(" "));
    
    // Parse tokens into AST using recursive descent
    let tokenIndex = 0;
    
    const parseExpression = (depth: number = 0): RuleGroup | RuleCondition => {
      const items: Array<RuleGroup | RuleCondition> = [];
      const operators: BoolOp[] = [];
      
      console.log(`  parseExpression depth=${depth}, tokenIndex=${tokenIndex}`);
      
      while (tokenIndex < tokens.length) {
        const token = tokens[tokenIndex];
        console.log(`    Token[${tokenIndex}]: ${token.type} = ${token.value}`);
        
        if (token.type === "paren_close") {
          // End of this group - don't consume the token, let caller handle it
          console.log(`    Ending group at depth ${depth}`);
          break;
        }
        
        if (token.type === "paren_open") {
          // Start of nested group
          tokenIndex++; // consume (
          const subExpr = parseExpression(depth + 1);
          if (tokenIndex < tokens.length && tokens[tokenIndex].type === "paren_close") {
            tokenIndex++; // consume )
          }
          items.push(subExpr);
          console.log(`    Added nested group with ${isCondition(subExpr) ? "condition" : (subExpr as RuleGroup).children.length + " children"}`);
        } else if (token.type === "placeholder") {
          // Extract condition index from {N}
          const condIndex = parseInt(token.value.substring(1, token.value.length - 1));
          if (condIndex >= 0 && condIndex < conditions.length) {
            items.push(conditions[condIndex]);
            console.log(`    Added condition {${condIndex}}: ${conditions[condIndex].variable}`);
          }
          tokenIndex++;
        } else if (token.type === "and" || token.type === "or") {
          const op = token.type === "and" ? "AND" : "OR";
          operators.push(op);
          console.log(`    Operator: ${op}`);
          tokenIndex++;
        } else {
          tokenIndex++;
        }
      }
      
      console.log(`  Finished parsing at depth ${depth}: ${items.length} items, ${operators.length} operators`);
      
      // If we only got one item and we're at root, return it
      if (items.length === 1 && depth === 0) {
        return items[0];
      }
      
      // If we only got one item in a nested expression, unwrap it
      if (items.length === 1 && depth > 0) {
        return items[0];
      }
      
      // Determine the operator for this group
      const uniqueOps = Array.from(new Set(operators));
      const groupOp: BoolOp = uniqueOps.length === 1 ? uniqueOps[0] : (operators[0] || "AND");
      
      console.log(`  Creating group with op=${groupOp}, ${items.length} children`);
      
      // Create group
      const group: RuleGroup = {
        id: generateId(),
        op: groupOp,
        children: items
      };
      
      return group;
    };
    
    const result = parseExpression(0);
    
    // Wrap in root if needed
    if (isCondition(result)) {
      console.log("🔄 Result is a single condition, wrapping in root");
      return {
        id: "root",
        op: "AND",
        children: [result]
      };
    }
    
    // If result is already a group, make it the root
    console.log("🔄 Result is a group, using as root");
    return {
      ...result,
      id: "root"
    };
  };

  // Update expression preview
  useEffect(() => {
    if (activeTab === "builder") {
      const expr = toExpression(ruleTree, true);
      setExpressionPreview(expr);
    } else {
      setExpressionPreview(advancedExpression);
    }
  }, [ruleTree, advancedExpression, activeTab]);

  // Initialize form when editing
  useEffect(() => {
    if (!isOpen) {
      setShowTestPanel(false);
      setTestResult(null);
      setTestError("");
      setTestInputs({});
      setTestLoading(false);
      return;
    }

    if (editingRule) {
      setRouteTarget(editingRule.route);
      setAdvancedExpression(editingRule.if);
      setRuleLabel(editingRule.label || "");
      
      if (editingRule.route.type === "agent" && editingRule.route.agent_id) {
        const targetAgent = findSubAgentById(editingRule.route.agent_id, agent);
        if (targetAgent && targetAgent.actions) {
          setSelectedAgentActions(targetAgent.actions);
        }
      }
      
      // Convert backend conditions to AST
      if (editingRule.conditions && editingRule.conditions.length > 0) {
        const tree = buildTreeFromBackendConditions(
          editingRule.conditions, 
          editingRule.condition_expression_template
        );
        setRuleTree(tree);
        setActiveTab("builder");
      } else {
        setRuleTree({ id: "root", op: "AND", children: [] });
        setActiveTab("advanced");
      }
    } else {
      setRouteTarget({ type: "agent" });
      setAdvancedExpression("");
      setRuleTree({ id: "root", op: "AND", children: [] });
      setRuleLabel("");
      setActiveTab("builder");
    }
    setValidationError("");
    setHasUnsaved(false);
  }, [editingRule, isOpen]);

  // Track unsaved changes
  useEffect(() => {
    if (!isOpen) return;
    setHasUnsaved(true);
  }, [routeTarget, advancedExpression, ruleTree, ruleLabel, isOpen]);

  // Add condition to a group
  const addCondition = (groupId: string) => {
    const newCondition: RuleCondition = {
      id: generateId(),
      variable: "",
      operator: "",
      value: ""
    };

    const addToGroup = (node: RuleGroup): RuleGroup => {
      if (node.id === groupId) {
        return { ...node, children: [...node.children, newCondition] };
      }
      return {
        ...node,
        children: node.children.map(child => 
          isCondition(child) ? child : addToGroup(child)
        )
      };
    };

    setRuleTree(addToGroup(ruleTree));
  };

  // Add group to a group
  const addGroup = (parentGroupId: string) => {
    const newGroup: RuleGroup = {
      id: generateId(),
      op: "AND",
      children: []
    };

    const addToGroup = (node: RuleGroup): RuleGroup => {
      if (node.id === parentGroupId) {
        return { ...node, children: [...node.children, newGroup] };
      }
      return {
        ...node,
        children: node.children.map(child => 
          isCondition(child) ? child : addToGroup(child)
        )
      };
    };

    setRuleTree(addToGroup(ruleTree));
  };

  // Remove node from tree
  const removeNode = (nodeId: string) => {
    const removeFromGroup = (node: RuleGroup): RuleGroup => {
      return {
        ...node,
        children: node.children
          .filter(child => {
            if (isCondition(child)) return child.id !== nodeId;
            return child.id !== nodeId;
          })
          .map(child => isCondition(child) ? child : removeFromGroup(child))
      };
    };

    setRuleTree(removeFromGroup(ruleTree));
  };

  // Update condition
  const updateCondition = (conditionId: string, field: keyof RuleCondition, value: string) => {
    const updateInGroup = (node: RuleGroup | RuleCondition): RuleGroup | RuleCondition => {
      if (isCondition(node)) {
        if (node.id === conditionId) {
          const updated = { ...node, [field]: value };
          
          // Reset comparison fields when operator changes
          if (field === "operator" && value !== "sem_similarity" && value !== "sentiment") {
            updated.comparisonOp = undefined;
            updated.comparedValue = undefined;
          }
          
          if (field === "operator" && value === "sentiment") {
            updated.comparisonOp = "IS";
          }
          
          return updated;
        }
        return node;
      }
      return {
        ...node,
        children: node.children.map(updateInGroup)
      };
    };

    setRuleTree(updateInGroup(ruleTree) as RuleGroup);
  };

  // Update group
  const updateGroup = (groupId: string, field: keyof RuleGroup, value: any) => {
    const updateInTree = (node: RuleGroup): RuleGroup => {
      if (node.id === groupId) {
        return { ...node, [field]: value };
      }
      return {
        ...node,
        children: node.children.map(child => 
          isCondition(child) ? child : updateInTree(child)
        )
      };
    };

    setRuleTree(updateInTree(ruleTree));
  };

  // Drag and Drop - New Architecture
  // ===================================
  
  // Find a node in the tree and return it along with its parent info
  const findNodeInTree = (nodeId: string): { 
    node: RuleGroup | RuleCondition; 
    parentId: string; 
    indexInParent: number;
  } | null => {
    const search = (group: RuleGroup): { node: RuleGroup | RuleCondition; parentId: string; indexInParent: number } | null => {
      for (let i = 0; i < group.children.length; i++) {
        const child = group.children[i];
        const childId = isCondition(child) ? child.id : child.id;
        
        if (childId === nodeId) {
          return { node: child, parentId: group.id, indexInParent: i };
        }
        
        // Recurse into groups
        if (!isCondition(child)) {
          const found = search(child);
          if (found) return found;
        }
      }
      return null;
    };
    
    return search(ruleTree);
  };
  
  // Check if dropping a group into itself or its descendants (prevent infinite loops)
  const wouldCreateCycle = (draggedNode: RuleGroup | RuleCondition, targetGroupId: string): boolean => {
    // Get the dragged node's ID
    const draggedId = isCondition(draggedNode) ? draggedNode.id : draggedNode.id;
    
    // Can't drop into itself
    if (draggedId === targetGroupId) {
      console.log("🚫 Cycle: Can't drop group into itself");
      return true;
    }
    
    // Conditions can't create cycles
    if (isCondition(draggedNode)) {
      return false;
    }
    
    // Check if target group is a descendant of the dragged group
    // This prevents dropping a group into its own children/grandchildren
    const isDescendantOf = (searchInGroup: RuleGroup, targetId: string): boolean => {
      for (const child of searchInGroup.children) {
        if (isCondition(child)) {
          continue;
        }
        
        if (child.id === targetId) {
          return true;
        }
        
        if (isDescendantOf(child, targetId)) {
          return true;
        }
      }
      return false;
    };
    
    const isCycle = isDescendantOf(draggedNode as RuleGroup, targetGroupId);
    
    if (isCycle) {
      console.log("🚫 Cycle: Target group is a descendant of dragged group");
    }
    
    return isCycle;
  };
  
  // Move a node to a new location
  const moveNode = (draggedId: string, targetGroupId: string, targetIndex: number) => {
    console.log("📦 Moving node:", draggedId, "to group:", targetGroupId, "at index:", targetIndex);
    
    // Find the dragged node
    const nodeInfo = findNodeInTree(draggedId);
    if (!nodeInfo) {
      console.warn("❌ Could not find dragged node:", draggedId);
      return;
    }
    
    const { node: draggedNode, parentId: sourceParentId, indexInParent: sourceIndex } = nodeInfo;
    console.log("  Source parent:", sourceParentId, "index:", sourceIndex);
    
    // Prevent cycles (pass the actual node, not just the ID)
    if (wouldCreateCycle(draggedNode, targetGroupId)) {
      console.warn("❌ Cannot drop a group into itself or its descendants");
      return;
    }
    
    // Calculate adjusted target index if moving within same parent
    let adjustedIndex = targetIndex;
    if (sourceParentId === targetGroupId && sourceIndex < targetIndex) {
      // Moving down in the same parent - adjust for removal
      adjustedIndex = targetIndex - 1;
      console.log("  Adjusted index from", targetIndex, "to", adjustedIndex, "(same parent)");
    }
    
    // Step 1: Remove node from source
    const removeNode = (group: RuleGroup): RuleGroup => {
      if (group.id === sourceParentId) {
        return {
          ...group,
          children: group.children.filter((_, idx) => idx !== sourceIndex)
        };
      }
      
      return {
        ...group,
        children: group.children.map(child => 
          isCondition(child) ? child : removeNode(child)
        )
      };
    };
    
    const treeWithoutDragged = removeNode(ruleTree);
    
    // Step 2: Insert node at target
    const insertNode = (group: RuleGroup): RuleGroup => {
      if (group.id === targetGroupId) {
        const newChildren = [...group.children];
        newChildren.splice(adjustedIndex, 0, draggedNode);
        console.log("✅ Inserted at index", adjustedIndex, "- new children count:", newChildren.length);
        return {
          ...group,
          children: newChildren
        };
      }
      
      return {
        ...group,
        children: group.children.map(child => 
          isCondition(child) ? child : insertNode(child)
        )
      };
    };
    
    const newTree = insertNode(treeWithoutDragged);
    setRuleTree(newTree);
    setHasUnsaved(true);
  };
  
  // Drag handlers
  const handleDragStart = (e: React.DragEvent, nodeId: string) => {
    console.log("🎯 Drag started for node:", nodeId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", nodeId);
    
    // Set dragged node immediately
    setDraggedNodeId(nodeId);
    
    // Small delay before enabling drop zones to prevent interference
    setTimeout(() => {
      setIsDragging(true);
    }, 50);
  };
  
  const handleDragEnd = (e: React.DragEvent) => {
    console.log("🏁 Drag ended");
    e.preventDefault();
    setDraggedNodeId(null);
    setActiveDropZone(null);
    setIsDragging(false);
  };
  
  const handleDropZoneEnter = (e: React.DragEvent, groupId: string, index: number) => {
    if (draggedNodeId) {
      setActiveDropZone({ groupId, index });
    }
  };
  
  const handleDropZoneDrop = (e: React.DragEvent, groupId: string, index: number) => {
    console.log("💧 Drop on zone:", groupId, "index:", index, "dragged:", draggedNodeId);
    
    if (draggedNodeId) {
      moveNode(draggedNodeId, groupId, index);
    }
    
    // Always clean up state
    setDraggedNodeId(null);
    setActiveDropZone(null);
    setIsDragging(false);
  };

  // Syntax highlight the expression
  const highlightExpression = (expr: string): React.ReactNode => {
    if (!expr) return null;
    
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    let parenDepth = 0;
    const parenStack: ("group" | "method")[] = [];
    
    const depthColors = [
      "#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444"
    ];
    
    const tokenRegex = /(\.sem_match\(|\.sem_similarity\(|\.like\(|\.matches\(|\.classify\(|\.sentiment\(|NOT|\(|\)|AND|OR|==|!=|>=|<=|>|<|[0-9]+\.?[0-9]*|"[^"]*"|\/[^\/]*\/[gi]*)/gi;
    
    let match;
    tokenRegex.lastIndex = 0;
    
    while ((match = tokenRegex.exec(expr)) !== null) {
      if (match.index > currentIndex) {
        const text = expr.substring(currentIndex, match.index);
        if (text.trim()) {
          parts.push(
            <span key={`text-${currentIndex}`} className="text-blue-600 dark:text-blue-400">
              {text}
            </span>
          );
        } else {
          parts.push(<span key={`space-${currentIndex}`}>{text}</span>);
        }
      }
      
      const token = match[0];
      
      if (token === "AND" || token === "OR") {
        parts.push(
          <span key={`token-${match.index}`} className="text-emerald-600 dark:text-emerald-400 font-semibold">
            {token}
          </span>
        );
      } else if (token === "NOT") {
        parts.push(
          <span key={`token-${match.index}`} className="text-red-600 dark:text-red-400 font-semibold">
            {token}
          </span>
        );
      } else if (token.startsWith(".sem_match") || token.startsWith(".sem_similarity") || token.startsWith(".like") || token.startsWith(".matches") || token.startsWith(".classify") || token.startsWith(".sentiment")) {
        parenStack.push("method");
        parts.push(
          <span key={`token-${match.index}`} className="text-cyan-600 dark:text-cyan-400">
            {token}
          </span>
        );
      } else if (token === "(") {
        parenStack.push("group");
        parenDepth++;
        const color = depthColors[(parenDepth - 1) % depthColors.length];
        parts.push(
          <span key={`token-${match.index}`} style={{ color }} className="font-bold">
            {token}
          </span>
        );
      } else if (token === ")") {
        const parenType = parenStack.pop();
        if (parenType === "group") {
          const color = depthColors[(parenDepth - 1) % depthColors.length];
          parts.push(
            <span key={`token-${match.index}`} style={{ color }} className="font-bold">
              {token}
            </span>
          );
          parenDepth--;
        } else {
          parts.push(
            <span key={`token-${match.index}`} className="text-cyan-600 dark:text-cyan-400">
              {token}
            </span>
          );
        }
      } else if (["==", "!=", ">=", "<=", ">", "<"].includes(token)) {
        parts.push(
          <span key={`token-${match.index}`} className="text-orange-600 dark:text-orange-400">
            {token}
          </span>
        );
      } else if (token.startsWith("\"") || token.startsWith("/") || /^[0-9]/.test(token)) {
        parts.push(
          <span key={`token-${match.index}`} className="text-rose-600 dark:text-rose-400">
            {token}
          </span>
        );
      } else {
        parts.push(<span key={`token-${match.index}`}>{token}</span>);
      }
      
      currentIndex = match.index + token.length;
    }
    
    if (currentIndex < expr.length) {
      const text = expr.substring(currentIndex);
      if (text.trim()) {
        parts.push(
          <span key={`text-${currentIndex}`} className="text-blue-600 dark:text-blue-400">
            {text}
          </span>
        );
      } else {
        parts.push(<span key={`space-${currentIndex}`}>{text}</span>);
      }
    }
    
    return <>{parts}</>;
  };

  // Validate expression
  const validateExpression = (expression: string): string => {
    if (!expression.trim()) {
      return "Expression cannot be empty";
    }

    const openParens = (expression.match(/\(/g) || []).length;
    const closeParens = (expression.match(/\)/g) || []).length;
    
    if (openParens !== closeParens) {
      return "Unbalanced parentheses";
    }

    const doubleQuotes = (expression.match(/"/g) || []).length;
    if (doubleQuotes % 2 !== 0) {
      return "Unbalanced quotes";
    }

    return "";
  };

  // Get inline regex error
  const getInlineRegexError = (condition: RuleCondition): string => {
    if (condition.operator !== "matches") return "";
    try {
      new RegExp(condition.value || "", "i");
      return "";
    } catch (e) {
      return "Invalid regex (omit surrounding / /)";
    }
  };

  // Save handler
  const handleSave = () => {
    const expression = activeTab === "builder" ? expressionPreview : advancedExpression;
    const error = validateExpression(expression);
    
    if (error) {
      setValidationError(error);
      return;
    }

    if (routeTarget.type === "agent" && !routeTarget.agent_id) {
      setValidationError("Please select an agent for the route target");
      return;
    }
    if (routeTarget.type === "action" && !routeTarget.action_id) {
      setValidationError("Please select an action for the route target");
      return;
    }

    // Convert tree to backend format
    const { template, conditions: astConditions, logicalOps } = toTemplateAndConditions(ruleTree);
    
    // Compute grouping from template
    const { groupStarts, groupEnds } = computeGroupingFromTemplate(template, astConditions.length);
    
    // Convert conditions to backend format using centralized logic
    const backendConditions: RoutingRuleCondition[] = astConditions.map((cond, index) => {
      const converted = convertConditionToBackend(cond, logicalOps[index]);
      return {
        ...converted,
        group_start: groupStarts[index],
        group_end: groupEnds[index]
      } as any;
    });

    const rule: RoutingRule = {
      id: editingRule?.id || `rule_${Date.now()}`,
      if: expression,
      condition_expression_template: template,
      conditions: backendConditions,
      route: routeTarget,
      enabled: true,
      label: ruleLabel.trim() || undefined,
    };

    console.log("💾 RoutingRuleEditor - Saving rule");
    console.log("  Template:", template);
    console.log("  Expression:", expression);
    console.log("  Conditions:", backendConditions.map((c, i) => ({
      index: i,
      variable: `${c.target_type}.${c.variable_path}`,
      operator: c.operator,
      logical_op: c.logical_op,
      group_start: c.group_start,
      group_end: c.group_end
    })));
    console.log("  Full payload:", rule);
    
    onSave(rule);
  };

  // Format route target
  const formatRouteTarget = (route: RouteTarget): string => {
    switch (route.type) {
      case "agent":
        const agent = subAgents.find(a => a.id === route.agent_id);
        const agentName = agent ? agent.agent_name : `Agent #${route.agent_id}`;
        if (route.action_id) {
          const action = selectedAgentActions.find(a => a.id === route.action_id);
          const actionName = action ? action.action_name : `Action #${route.action_id}`;
          return `${agentName} → ${actionName}`;
        }
        return agentName;
      case "action":
        const action = actions.find(a => a.id === route.action_id);
        return action ? action.action_name : `Action #${route.action_id}`;
      case "llm":
        return "LLM";
      case "none":
        return "None";
      default:
        return "Unknown";
    }
  };

  // Get unique variables from AST for testing
  const getUniqueVariables = () => {
    const uniqueVars = new Map<string, { variable: string; fieldType: string }>();
    
    const collectVarsFromNode = (node: RuleGroup | RuleCondition) => {
      if (isCondition(node)) {
        if (node.variable && node.operator && (node.value || node.operator === "is_null" || node.operator === "is_not_null")) {
          if (!uniqueVars.has(node.variable)) {
            let fieldType = "string";
            if (node.variable.startsWith("state.")) {
              const fieldName = node.variable.substring(6);
              const formField = formFields.find(f => f.name === fieldName);
              if (formField) {
                fieldType = formField.type;
              }
            }
            uniqueVars.set(node.variable, { variable: node.variable, fieldType });
          }
        }
      } else {
        node.children.forEach(collectVarsFromNode);
      }
    };
    
    collectVarsFromNode(ruleTree);
    return Array.from(uniqueVars.values());
  };

  const clearTestResults = () => {
    setTestResult(null);
    setTestError("");
  };

  const runTest = () => {
    const typedInputs: Record<string, any> = {};
    for (const [key, value] of Object.entries(testInputs)) {
      const variables = getUniqueVariables();
      const variable = variables.find(v => v.variable === key);
      if (!variable) continue;

      if (value === "" || value === null || value === undefined) {
        continue;
      }

      if (variable.fieldType === "boolean") {
        typedInputs[key] = value === "true" || value === true;
      } else if (variable.fieldType === "number" || variable.fieldType === "integer" || variable.fieldType === "float") {
        typedInputs[key] = parseFloat(value);
      } else {
        typedInputs[key] = value;
      }
    }

    setTestLoading(true);
    setTestError("");

    const { template, conditions: astConditions, logicalOps } = toTemplateAndConditions(ruleTree);
    const { groupStarts, groupEnds } = computeGroupingFromTemplate(template, astConditions.length);
    
    const backendConditions = astConditions.map((cond, index) => {
      const converted = convertConditionToBackend(cond, logicalOps[index]);
      return {
        ...converted,
        group_start: groupStarts[index],
        group_end: groupEnds[index]
      };
    });

    console.log("🔍 Test rule - Generated template:", template);
    console.log("🔍 Test rule - Backend conditions:", backendConditions);

    const ruleId = editingRule?.id ? Number(editingRule.id) : 0;

    RoutingService.testRule(agent.id, ruleId, typedInputs, apiKey, backendConditions, template).subscribe({
      next: (result) => {
        setTestResult(result);
        setTestLoading(false);
      },
      error: (error) => {
        console.error("Error testing rule:", error);
        setTestError(error?.message || "Failed to test rule. Please try again.");
        setTestLoading(false);
      }
    });
  };

  // Compute grouping from template helper (same as in handleSave)
  const computeGroupingFromTemplate = (template: string, conditionsCount: number) => {
    const groupStarts: number[] = new Array(conditionsCount).fill(0);
    const groupEnds: number[] = new Array(conditionsCount).fill(0);
    
    let parenStack = 0;
    let currentCondIndex = -1;
    let i = 0;
    
    while (i < template.length) {
      if (template[i] === "(") {
        parenStack++;
        i++;
      } else if (template[i] === ")") {
        if (currentCondIndex >= 0) {
          groupEnds[currentCondIndex]++;
        }
        parenStack--;
        i++;
      } else if (template[i] === "{") {
        const endBrace = template.indexOf("}", i);
        if (endBrace > i) {
          const condIndex = parseInt(template.substring(i + 1, endBrace));
          currentCondIndex = condIndex;
          groupStarts[condIndex] = parenStack;
          i = endBrace + 1;
        } else {
          i++;
        }
      } else if (template.substring(i).match(/^(AND|OR)/)) {
        const match = template.substring(i).match(/^(AND|OR)/);
        if (match) {
          i += match[0].length;
        } else {
          i++;
        }
      } else {
        i++;
      }
    }
    
    return { groupStarts, groupEnds };
  };

  // Render a drop zone
  const renderDropZone = (groupId: string, index: number): React.ReactNode => {
    const isActive = activeDropZone?.groupId === groupId && activeDropZone?.index === index;
    const isVisible = isDragging;  // Only show when actively dragging
    
    if (!isVisible) return null;
    
    return (
      <div
        className={cn(
          "transition-all duration-150 rounded-md border-2 border-dashed flex items-center justify-center",
          isActive 
            ? "h-12 bg-maitai-green/10 border-maitai-green" 
            : "h-8 bg-muted/30 border-muted-foreground/20 hover:border-maitai-green/50 hover:bg-maitai-green/5"
        )}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDropZoneEnter(e, groupId, index);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDropZoneDrop(e, groupId, index);
        }}
      >
        {isActive && (
          <span className="text-xs font-gc-med text-maitai-green">Drop here</span>
        )}
      </div>
    );
  };

  // Render a group recursively
  const renderGroup = (group: RuleGroup, depth: number = 0, isRoot: boolean = false): React.ReactNode => {
    const depthColors = ["#e0e0e0", "#10b981", "#3b82f6", "#f59e0b", "#8b5cf6"];
    const borderColor = depthColors[Math.min(depth, depthColors.length - 1)];
    const isDraggingThisGroup = draggedNodeId === group.id;
    
    return (
      <div
        key={group.id}
        className={cn(
          "space-y-3 w-full max-w-full overflow-hidden transition-all duration-200",
          !isRoot && "p-4 border-2 rounded-lg",
          group.not && "bg-red-50/50 dark:bg-red-950/10",
          isDraggingThisGroup && "opacity-50"
        )}
        style={{ borderColor: !isRoot ? borderColor : undefined }}
      >
        {/* Group header */}
        {!isRoot ? (
          <div className="flex items-center justify-between gap-3 pb-2 border-b">
            <div className="flex items-center gap-3">
              <div
                draggable={true}
                onDragStart={(e) => {
                  e.stopPropagation();
                  handleDragStart(e, group.id);
                }}
                onDragEnd={(e) => {
                  e.stopPropagation();
                  handleDragEnd(e);
                }}
                className="cursor-grab active:cursor-grabbing select-none flex items-center px-1.5 py-1 hover:bg-muted/70 rounded transition-colors"
                style={{ userSelect: "none", WebkitUserSelect: "none", touchAction: "none" }}
                title="Drag to reorder"
              >
                <GripVerticalIcon className="w-5 h-5 text-muted-foreground shrink-0" />
              </div>
              <FoldersIcon className="w-4 h-4 text-muted-foreground" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Match</span>
                <div className="flex items-center gap-1">
                  <span
                    role="button"
                    onClick={() => updateGroup(group.id, "op", "AND")}
                    className={cn(
                      "cursor-pointer text-sm select-none px-2 py-0.5 rounded",
                      group.op === "AND"
                        ? "bg-maitai-green text-white font-gc-med"
                        : "text-muted-foreground hover:bg-muted"
                    )}
                  >
                    ALL
                  </span>
                  <span
                    role="button"
                    onClick={() => updateGroup(group.id, "op", "OR")}
                    className={cn(
                      "cursor-pointer text-sm select-none px-2 py-0.5 rounded",
                      group.op === "OR"
                        ? "bg-maitai-green text-white font-gc-med"
                        : "text-muted-foreground hover:bg-muted"
                    )}
                  >
                    ANY
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">of the following:</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateGroup(group.id, "not", !group.not)}
                className={cn(
                  "h-7 px-2 text-xs",
                  group.not && "bg-red-100 dark:bg-red-950/30 text-red-600"
                )}
              >
                NOT
              </Button>
            </div>
            {!isRoot && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeNode(group.id)}
                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
              >
                <XIcon className="w-4 h-4" />
              </Button>
            )}
          </div>
        ) : (
          // Root-level operator selector
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Match</span>
              <div className="flex items-center gap-1">
                <span
                  role="button"
                  onClick={() => updateGroup(group.id, "op", "AND")}
                  className={cn(
                    "cursor-pointer text-sm select-none px-3 py-1 rounded",
                    group.op === "AND"
                      ? "bg-maitai-green text-white font-gc-med"
                      : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  ALL
                </span>
                <span
                  role="button"
                  onClick={() => updateGroup(group.id, "op", "OR")}
                  className={cn(
                    "cursor-pointer text-sm select-none px-3 py-1 rounded",
                    group.op === "OR"
                      ? "bg-maitai-green text-white font-gc-med"
                      : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  ANY
                </span>
              </div>
              <span className="text-sm text-muted-foreground">of the following conditions:</span>
            </div>
          </div>
        )}

        {/* Children */}
        <div className="space-y-2 w-full max-w-full overflow-hidden">
          {group.children.length === 0 ? (
            // Empty group - show single drop zone
            <div className="p-4">
              {isDragging ? (
                renderDropZone(group.id, 0)
              ) : (
                <div className="p-8 border-2 border-dashed rounded-lg text-center text-sm text-muted-foreground">
                  No conditions yet. Add a condition or group below.
                </div>
              )}
            </div>
          ) : (
            // Group has children - show drop zones between items
            <>
              {group.children.map((child, idx) => {
                const childId = isCondition(child) ? child.id : child.id;
                
                return (
                  <React.Fragment key={childId}>
                    {/* Drop zone before this child */}
                    {renderDropZone(group.id, idx)}
                    
                    {/* The child itself */}
                    {isCondition(child) 
                      ? renderCondition(child, group.id, idx)
                      : renderGroup(child, depth + 1, false)}
                  </React.Fragment>
                );
              })}
              
              {/* Drop zone after all children */}
              {renderDropZone(group.id, group.children.length)}
            </>
          )}
        </div>

        {/* Add buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => addCondition(group.id)}
            variant="outline"
            size="sm"
            className="gap-2 border-dashed"
          >
            <PlusIcon className="w-4 h-4" />
            Add Condition
          </Button>
          <Button
            onClick={() => addGroup(group.id)}
            variant="outline"
            size="sm"
            className="gap-2 border-dashed"
          >
            <FoldersIcon className="w-4 h-4" />
            Add Group
          </Button>
        </div>
      </div>
    );
  };

  // Render a single condition
  const renderCondition = (condition: RuleCondition, groupId: string, index: number): React.ReactNode => {
    const varInfo = availableVariables.find(v => v.value === condition.variable);
    const fieldType = (varInfo as any)?.fieldType || "string";
    const isDraggingThis = draggedNodeId === condition.id;

    return (
      <div 
        key={condition.id} 
        className={cn(
          "flex items-center gap-3 p-3 border rounded bg-card min-w-0 w-full max-w-full overflow-hidden transition-all duration-200",
          isDraggingThis && "opacity-50"
        )}
      >
        <div
          draggable={true}
          onDragStart={(e) => {
            e.stopPropagation();
            handleDragStart(e, condition.id);
          }}
          onDragEnd={(e) => {
            e.stopPropagation();
            handleDragEnd(e);
          }}
          className="cursor-grab active:cursor-grabbing select-none flex items-center px-1.5 py-1 hover:bg-muted/70 rounded transition-colors"
          style={{ userSelect: "none", WebkitUserSelect: "none", touchAction: "none" }}
          title="Drag to reorder"
        >
          <GripVerticalIcon className="w-5 h-5 text-muted-foreground shrink-0" />
        </div>

        {/* Variable selector */}
        <Popover 
          open={openPopovers[condition.id] || false} 
          onOpenChange={(open) => setOpenPopovers(prev => ({ ...prev, [condition.id]: open }))}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-[300px] min-w-0 shrink-0 justify-between font-normal"
            >
              {condition.variable ? (
                <div className="flex flex-col items-start min-w-0 flex-1">
                  <span className="font-mono text-sm truncate w-full">{condition.variable}</span>
                  <span className="text-xs text-muted-foreground truncate w-full">
                    {availableVariables.find(v => v.value === condition.variable)?.description}
                  </span>
                </div>
              ) : (
                "Select variable..."
              )}
              <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0">
            <Command>
              <CommandInput placeholder="Search variables..." />
              <div className="max-h-[300px] overflow-y-auto">
                <CommandList>
                  <CommandEmpty>No variable found.</CommandEmpty>
                  <CommandGroup>
                    {availableVariables.map((var_) => (
                      <CommandItem
                        key={var_.value}
                        value={var_.value}
                        onSelect={() => {
                          updateCondition(condition.id, "variable", var_.value);
                          setOpenPopovers(prev => ({ ...prev, [condition.id]: false }));
                        }}
                      >
                        <div className="space-y-1">
                          <div className="font-mono text-sm leading-tight">{var_.label}</div>
                          <div className="text-xs text-muted-foreground leading-tight whitespace-normal">{var_.description}</div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </div>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Operator selector */}
        <div className="flex items-center gap-2">
          <Select
            value={condition.operator}
            onValueChange={(value) => updateCondition(condition.id, "operator", value)}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Op..." />
            </SelectTrigger>
            <SelectContent>
              {getOperatorsForVariable(condition.variable).map((op) => (
                <SelectItem key={op.value} value={op.value}>
                  {op.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Tooltip content="regex: omit / /; sem_match: binary meaning match; sem_similarity: score 0-1 with threshold">
            <Info className="w-3 h-3 text-muted-foreground" />
          </Tooltip>
        </div>

        {/* Value input */}
        {(() => {
          if (condition.operator === "is_null" || condition.operator === "is_not_null") {
            return <div className="w-[200px] min-w-0" />;
          }
          
          if (condition.operator === "classify" || condition.operator === "not_classify") {
            return (
              <Select
                value={condition.value}
                onValueChange={(value) => updateCondition(condition.id, "value", value)}
              >
                <SelectTrigger className="w-[200px] min-w-0">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AFFIRMATIVE">Affirmative</SelectItem>
                  <SelectItem value="NEGATIVE">Negative</SelectItem>
                  <SelectItem value="QUESTION">Question</SelectItem>
                </SelectContent>
              </Select>
            );
          }
          
          if (condition.operator === "sentiment") {
            return (
              <>
                <Select
                  value={condition.comparisonOp || "IS"}
                  onValueChange={(value) => updateCondition(condition.id, "comparisonOp", value)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IS">IS</SelectItem>
                    <SelectItem value="IS_NOT">IS NOT</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={condition.value}
                  onValueChange={(value) => updateCondition(condition.id, "value", value)}
                >
                  <SelectTrigger className="w-[200px] min-w-0">
                    <SelectValue placeholder="Select sentiment..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="POSITIVE">Positive</SelectItem>
                    <SelectItem value="NEGATIVE">Negative</SelectItem>
                    <SelectItem value="NEUTRAL">Neutral</SelectItem>
                  </SelectContent>
                </Select>
              </>
            );
          }
          
          if (fieldType === "boolean") {
            return (
              <Select
                value={condition.value}
                onValueChange={(value) => updateCondition(condition.id, "value", value)}
              >
                <SelectTrigger className="w-[200px] min-w-0">
                  <SelectValue placeholder="Value..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">true</SelectItem>
                  <SelectItem value="false">false</SelectItem>
                  <SelectItem value="null">null</SelectItem>
                </SelectContent>
              </Select>
            );
          }
          
          if (fieldType === "number" || fieldType === "integer" || fieldType === "float") {
            return (
              <Input
                type="number"
                value={condition.value}
                onChange={(e) => updateCondition(condition.id, "value", e.target.value)}
                placeholder="Value..."
                className="w-[200px] min-w-0"
              />
            );
          }
          
          return (
            <Input
              value={condition.value}
              onChange={(e) => updateCondition(condition.id, "value", e.target.value)}
              placeholder="Value..."
              className="w-[200px] min-w-0"
            />
          );
        })()}

        {/* Sem_similarity additional inputs */}
        {condition.operator === "sem_similarity" && (
          <>
            <Select
              value={condition.comparisonOp || ">"}
              onValueChange={(value) => updateCondition(condition.id, "comparisonOp", value)}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=">">&gt;</SelectItem>
                <SelectItem value=">=">&gt;=</SelectItem>
                <SelectItem value="<">&lt;</SelectItem>
                <SelectItem value="<=">&lt;=</SelectItem>
                <SelectItem value="==">=</SelectItem>
                <SelectItem value="!=">!=</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={condition.comparedValue || ""}
              onChange={(e) => updateCondition(condition.id, "comparedValue", e.target.value)}
              placeholder="0.85"
              className="w-20"
              type="number"
              step="0.01"
              min="0"
              max="1"
            />
          </>
        )}

        {/* Regex error */}
        {getInlineRegexError(condition) && (
          <div className="text-xs text-red-600">
            {getInlineRegexError(condition)}
          </div>
        )}

        {/* Remove button */}
        <Button
          onClick={() => removeNode(condition.id)}
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
        >
          <XIcon className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (hasUnsaved) {
            setDiscardDialogOpen(true);
          } else {
            onClose();
          }
          return;
        }
      }}
    >
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingRule ? "Edit Routing Rule" : "New Routing Rule"}
          </DialogTitle>
          <DialogDescription>
            Define conditions using nested groups. Each group can match ALL (AND) or ANY (OR) of its conditions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Rule label */}
          <Card className="border-border">
            <CardContent className="p-4 space-y-2">
              <Label className="text-sm">Label (optional)</Label>
              <Input
                value={ruleLabel}
                onChange={(e) => setRuleLabel(e.target.value)}
                placeholder="e.g., FAQ route, Greeting start, Escalation"
              />
            </CardContent>
          </Card>

          {/* Route Target Selection */}
          <Card className="border-border">
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-base font-gc-med">Route To</Label>
                <p className="text-sm text-muted-foreground">
                  Where to route when this condition is met
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Select 
                  value={routeTarget.type}
                  onValueChange={(type) => setRouteTarget({ type: type as RouteTarget["type"] })}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Specific Agent</SelectItem>
                    <SelectItem value="action">Specific Action</SelectItem>
                    <SelectItem value="llm">LLM</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>

                {routeTarget.type === "agent" && (
                  <>
                    <Select 
                      value={String(routeTarget.agent_id || "")}
                      onValueChange={(agentId) => {
                        const targetAgentId = Number(agentId);
                        setRouteTarget({ 
                          type: "agent", 
                          agent_id: targetAgentId
                        });
                        
                        const targetAgent = findSubAgentById(targetAgentId, agent);
                        if (targetAgent && targetAgent.actions) {
                          setSelectedAgentActions(targetAgent.actions);
                        } else {
                          setSelectedAgentActions([]);
                        }
                      }}
                    >
                      <SelectTrigger className="w-72">
                        <SelectValue placeholder="Select agent..." />
                      </SelectTrigger>
                      <SelectContent>
                        {subAgents.map((sa) => (
                          <SelectItem key={`agent-${sa.id}`} value={String(sa.id)}>
                            {sa.agent_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {routeTarget.agent_id && (
                      <div className="flex items-center gap-2">
                        <Select 
                          value={routeTarget.action_id ? String(routeTarget.action_id) : undefined}
                          onValueChange={(actionId) => setRouteTarget({ 
                            ...routeTarget, 
                            action_id: Number(actionId)
                          })}
                        >
                          <SelectTrigger className="w-72">
                            <SelectValue placeholder={selectedAgentActions.length ? "Optional: Select action..." : "This agent has no actions"} />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedAgentActions.map((a) => (
                              <SelectItem key={`action-${a.id}`} value={String(a.id)}>
                                {a.action_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {routeTarget.action_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRouteTarget({ ...routeTarget, action_id: undefined })}
                            className="h-8 w-8 p-0"
                          >
                            <XIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}

                {routeTarget.type === "action" && (
                  <Select 
                    value={String(routeTarget.action_id || "")}
                    onValueChange={(actionId) => setRouteTarget({ type: "action", action_id: Number(actionId) })}
                  >
                    <SelectTrigger className="w-72">
                      <SelectValue placeholder="Select action..." />
                    </SelectTrigger>
                    <SelectContent>
                      {actions.map((a) => (
                        <SelectItem key={`action-${a.id}`} value={String(a.id)}>
                          {a.action_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {routeTarget.type !== "none" && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Info className="w-4 h-4" />
                  <span>Will route to: <strong>{formatRouteTarget(routeTarget)}</strong></span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Condition Builder */}
          <Card className="border-border">
            <CardContent className="p-4 space-y-4">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "builder" | "advanced")}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="builder" className="gap-2">
                    <WandIcon className="w-4 h-4" />
                    Builder
                  </TabsTrigger>
                  <TabsTrigger value="advanced" className="gap-2">
                    <CodeIcon className="w-4 h-4" />
                    Advanced
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="builder" className="space-y-4">
                  <div className={cn(
                    "relative rounded-lg transition-all duration-200",
                    draggedNodeId && "ring-2 ring-dashed ring-maitai-green/30 p-4"
                  )}>
                    {draggedNodeId && (
                      <div className="absolute -top-2 left-4 bg-background px-2 text-xs text-maitai-green font-gc-med">
                        Drop anywhere to reposition
                      </div>
                    )}
                    {renderGroup(ruleTree, 0, true)}
                  </div>
                </TabsContent>

                <TabsContent value="advanced" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Expression</Label>
                    <Textarea
                      value={advancedExpression}
                      onChange={(e) => setAdvancedExpression(e.target.value)}
                      placeholder="e.g., request.last_message.matches(/faq/i) || state.faq_asked == true"
                      className="font-mono text-sm min-h-[120px]"
                    />
                    <div className="text-xs text-muted-foreground">
                      Supports: state variables, request fields, regex (omit / /), semantic match, semantic similarity, logical operators
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Expression Preview */}
              {expressionPreview && (
                <div className="space-y-2">
                  <Label className="text-sm font-gc-med">Expression Preview</Label>
                  <div className="p-3 bg-muted rounded font-mono text-sm leading-relaxed">
                    {highlightExpression(expressionPreview)}
                  </div>
                </div>
              )}

              {/* Validation Error */}
              {validationError && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>{validationError}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Test Rule Panel */}
          {showTestPanel && (
            <Card className="border-purple-200 dark:border-purple-800">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <FlaskConicalIcon className="w-5 h-5 text-purple-500" />
                    <Label className="text-base font-gc-med">Test Rule</Label>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTestPanel(false)}
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>

                {getUniqueVariables().length === 0 ? (
                  <div className="text-sm text-muted-foreground p-8 bg-muted/50 rounded text-center">
                    No variables to test in this rule.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Variable Inputs */}
                    <div className="space-y-2">
                      {getUniqueVariables().map((variable) => (
                        <div key={variable.variable} className="flex items-center justify-between gap-4">
                          <Label className="font-mono text-xs text-muted-foreground">
                            {variable.variable}
                          </Label>
                          <div className="w-[240px]">
                            {variable.fieldType === "boolean" ? (
                              <Select
                                value={String(testInputs[variable.variable] ?? "")}
                                onValueChange={(val) => setTestInputs((prev) => ({ ...prev, [variable.variable]: val }))}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="true">true</SelectItem>
                                  <SelectItem value="false">false</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : variable.fieldType === "number" || variable.fieldType === "integer" || variable.fieldType === "float" ? (
                              <Input
                                type="number"
                                value={testInputs[variable.variable] ?? ""}
                                onChange={(e) => setTestInputs((prev) => ({ ...prev, [variable.variable]: e.target.value }))}
                                placeholder="0"
                                className="h-8 text-xs"
                              />
                            ) : (
                              <Input
                                type="text"
                                value={testInputs[variable.variable] ?? ""}
                                onChange={(e) => setTestInputs((prev) => ({ ...prev, [variable.variable]: e.target.value }))}
                                placeholder="Enter value..."
                                className="h-8 text-xs"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Action Button - Hidden when results are shown */}
                    {!testResult && (
                      <Button
                        onClick={runTest}
                        disabled={testLoading}
                        className="w-full gap-2"
                      >
                        {testLoading ? (
                          <>
                            <Loader2Icon className="w-4 h-4 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          <>
                            <FlaskConicalIcon className="w-4 h-4" />
                            Run Test
                          </>
                        )}
                      </Button>
                    )}

                    {/* Error Display */}
                    {testError && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{testError}</span>
                      </div>
                    )}

                    {/* Results */}
                    {testResult && (
                      <div className="space-y-3 pt-2 border-t">
                        {/* Overall Result */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-gc-med">Result</span>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={testResult.rule_passed ? "default" : "destructive"}
                              className={cn(
                                "gap-1",
                                testResult.rule_passed ? "bg-green-600" : "bg-red-600"
                              )}
                            >
                              {testResult.rule_passed ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  Passed
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-3 h-3" />
                                  Failed
                                </>
                              )}
                            </Badge>
                            <Button
                              onClick={clearTestResults}
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                            >
                              Clear
                            </Button>
                          </div>
                        </div>

                        {/* Condition Results */}
                        <div className="space-y-1">
                          {testResult.conditions.map((condResult, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 p-2 rounded text-xs bg-muted/30"
                            >
                              {condResult.passed ? (
                                <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                              ) : (
                                <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                              )}
                              <span className="font-mono text-muted-foreground flex-1 truncate">
                                {condResult.variable}
                              </span>
                              <span className="text-muted-foreground">
                                {condResult.operator} {JSON.stringify(condResult.expected)}
                              </span>
                              {condResult.similarity !== undefined && (
                                <Badge variant="outline" className="text-xs h-5">
                                  {(condResult.similarity * 100).toFixed(0)}%
                                </Badge>
                              )}
                              {(condResult as any).predicted_class !== undefined && (
                                <Badge variant="outline" className="text-xs h-5">
                                  {(condResult as any).predicted_class}
                                </Badge>
                              )}
                              {(condResult as any).predicted_sentiment !== undefined && (
                                <Badge variant="outline" className="text-xs h-5">
                                  {(condResult as any).predicted_sentiment}
                                </Badge>
                              )}
                              {(condResult as any).confidence !== undefined && (
                                <Badge variant="outline" className="text-xs h-5">
                                  {((condResult as any).confidence * 100).toFixed(0)}%
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              if (hasUnsaved) {
                setDiscardDialogOpen(true);
              } else {
                onClose();
              }
            }}
          >
            Cancel
          </Button>
          {!showTestPanel && (
            <Button
              variant="outline"
              onClick={() => {
                setShowTestPanel(true);
                // Initialize test inputs
                const initialInputs: Record<string, any> = {};
                getUniqueVariables().forEach(v => {
                  initialInputs[v.variable] = testInputs[v.variable] || "";
                });
                setTestInputs(initialInputs);
              }}
              className="gap-2"
            >
              <FlaskConicalIcon className="w-4 h-4" />
              Test
            </Button>
          )}
          <Button 
            onClick={handleSave}
            disabled={
              !expressionPreview || 
              !!validationError || 
              !routeTarget.type ||
              (routeTarget.type === "agent" && !routeTarget.agent_id) ||
              (routeTarget.type === "action" && !routeTarget.action_id)
            }
            className="gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            {editingRule ? "Save Changes" : "Add Rule"}
          </Button>
        </div>
      </DialogContent>

      {/* Discard Changes Confirmation Dialog */}
      <AlertDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDiscardDialogOpen(false);
                onClose();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default RoutingRuleEditor;


